import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import baseUrl from '../api/utils';
import axios from 'axios';
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const EwayBillSettingsForm = () => {
  const MySwal = withReactContent(Swal);
  const token = sessionStorage.getItem("token");
  const navigate = useNavigate();

  const [ewayBillCreds, setEwayBillCreds] = useState({
    ewbUsername: "",
    ewbPassword: "",
  });

  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [errors, setErrors] = useState({
    ewbUsername: "",
    ewbPassword: "",
  });

  const [loading, setLoading] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);

  const fetchEwayBillCredentials = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/api/eway-bill/credentials/status`, {
        headers: { "Authorization": token }
      });

      if (response.status === 200 && response.data.isConfigured) {
        setEwayBillCreds({
          ewbUsername: response.data.ewbUsername,
          ewbPassword: "••••••••", // password never sent back from backend, masked placeholder
        });
      } else {
        setIsNotFound(true);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/unauthorized");
      } else {
        setIsNotFound(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEwayBillCredentials();
  }, []);

  const handleInputsChange = (e) => {
    const { name, value } = e.target;
    let error = "";

    switch (name) {
      case 'ewbUsername':
        error = value.trim() === '' ? 'Please enter a valid E-Way Bill Username' : '';
        break;
      case 'ewbPassword':
        error = value.trim() === '' ? 'Please enter a valid E-Way Bill Password' : '';
        break;
      default:
        break;
    }

    setErrors(prev => ({ ...prev, [name]: error }));
    setEwayBillCreds(prev => ({ ...prev, [name]: value }));
  };

  const handleEdit = (e) => {
    e.preventDefault();
    setEwayBillCreds(prev => ({ ...prev, ewbPassword: "" })); // clear masked password before editing
    setIsNotFound(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const requiredFields = ['ewbUsername', 'ewbPassword'];
    let hasErrors = false;

    requiredFields.forEach(field => {
      if (!ewayBillCreds[field]?.trim()) {
        hasErrors = true;
        setErrors(prev => ({ ...prev, [field]: 'This field is required' }));
      }
    });

    if (hasErrors) return;

    try {
      const response = await axios.post(`${baseUrl}/api/eway-bill/credentials/save`, ewayBillCreds, {
        headers: { Authorization: token }
      });

      if (response.status === 200) {
        MySwal.fire({
          title: "Saved!",
          text: "E-Way Bill credentials saved successfully.",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => { fetchEwayBillCredentials(); });
      }
    } catch (error) {
      MySwal.fire({
        title: "Error",
        text: error.response?.data || "Failed to save credentials.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const Skeleton = (
    <div>
      <h4>E-Way Bill Settings</h4>

      <div className="form-group row">
        <label className="col-sm-3 col-form-label">
          E-Way Bill Username <span className="text-danger">*</span>
        </label>
        <div className="col-sm-9 input-group">
          <div className="skeleton skeleton-input"></div>
        </div>
      </div>

      <div className="form-group row">
        <label className="col-sm-3 col-form-label">
          E-Way Bill Password <span className="text-danger">*</span>
        </label>
        <div className="col-sm-9 input-group">
          <div className="skeleton skeleton-input"></div>
        </div>
      </div>

      <div className="btngrp">
        <div className="skeleton skeleton-button"></div>
      </div>
    </div>
  );

  const editableInputs = (
    <form onSubmit={handleSave}>
      <h4>E-Way Bill Settings</h4>
      <p className="text-muted">
        Enter your GST E-Way Bill Portal login credentials. This is a one-time setup.
      </p>

      <div className='form-group row'>
        <label htmlFor='ewbUsername' className='col-sm-3 col-form-label'>
          E-Way Bill Username <span className="text-danger">*</span>
        </label>
        <div className='col-sm-9 input-group'>
          <input
            id='ewbUsername'
            name='ewbUsername'
            type={showUsername ? 'text' : 'password'}
            value={ewayBillCreds.ewbUsername}
            onChange={handleInputsChange}
            className={`form-control ${errors.ewbUsername ? 'is-invalid' : ''}`}
            placeholder='Enter E-Way Bill Portal Username'
          />
          <div className="input-group-append">
            <button type="button" className="hidebtn" onClick={() => setShowUsername(!showUsername)}>
              <i className={`fa-solid ${showUsername ? 'fa-eye-slash' : 'fa-eye'}`}></i>
            </button>
          </div>
          {errors.ewbUsername && <div className="invalid-feedback">{errors.ewbUsername}</div>}
        </div>
      </div>

      <div className='form-group row'>
        <label htmlFor='ewbPassword' className='col-sm-3 col-form-label'>
          E-Way Bill Password <span className="text-danger">*</span>
        </label>
        <div className='col-sm-9 input-group'>
          <input
            id='ewbPassword'
            name='ewbPassword'
            type={showPassword ? 'text' : 'password'}
            value={ewayBillCreds.ewbPassword}
            onChange={handleInputsChange}
            className={`form-control ${errors.ewbPassword ? 'is-invalid' : ''}`}
            placeholder='Enter E-Way Bill Portal Password'
          />
          <div className="input-group-append">
            <button type="button" className="hidebtn" onClick={() => setShowPassword(!showPassword)}>
              <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
            </button>
          </div>
          {errors.ewbPassword && <div className="invalid-feedback">{errors.ewbPassword}</div>}
        </div>
      </div>

      <div className='btngrp'>
        <button type='submit' className='btn btn-primary'>Save</button>
      </div>
    </form>
  );

  const readOnlyInputs = (
    <div>
      <h4>E-Way Bill Settings</h4>

      <div className='form-group row'>
        <label className="col-sm-3 col-form-label">
          E-Way Bill Username <span className="text-danger">*</span>
        </label>
        <div className="col-sm-9 input-group">
          <input
            type={showUsername ? "text" : "password"}
            value={ewayBillCreds.ewbUsername}
            className='form-control'
            readOnly
          />
          <div className="input-group-append">
            <button
              type="button"
              className="hidebtn"
              onClick={() => setShowUsername(prev => !prev)}
            >
              <i className={`fa-solid ${showUsername ? 'fa-eye-slash' : 'fa-eye'}`}></i>
            </button>
          </div>
        </div>
      </div>

      <div className='form-group row'>
        <label className="col-sm-3 col-form-label">
          E-Way Bill Password <span className="text-danger">*</span>
        </label>
        <div className="col-sm-9 input-group">
          <input
            type={showPassword ? "text" : "password"}
            value={ewayBillCreds.ewbPassword}
            className='form-control'
            readOnly
          />
          <div className="input-group-append">
            <button
              type="button"
              className="hidebtn"
              onClick={() => setShowPassword(prev => !prev)}
            >
              <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
            </button>
          </div>
        </div>
      </div>

      <div className='btngrp'>
        <button className='btn btn-success' onClick={handleEdit}>Edit</button>
      </div>
    </div>
  );

  return (
    <div>
      <div className="page-header"></div>
      <div className="card" style={{ margin: "0 -25px 0 -20px" }}>
        <div className="card-body">
          <div className="row">
            <div className="col-12">
              <div className="navigateheaders">
                <div onClick={() => navigate(-1)}>
                  <i className="fa-solid fa-arrow-left"></i>
                </div>
                <div></div>
                <div onClick={() => navigate(-1)}>
                  <i className="fa-solid fa-xmark"></i>
                </div>
              </div>
              {loading ? Skeleton : isNotFound ? editableInputs : readOnlyInputs}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EwayBillSettingsForm;